import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Message,
  Progress,
  Radio,
  Space,
  Table,
  Typography,
} from "@arco-design/web-react";
import { ArrowLeft, Eraser, FileSpreadsheet, Upload as UploadIcon } from "lucide-react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { useOrderHandoverRuntime } from "../runtime";
import type {
  OrderHandoverRosterImportMode,
  OrderHandoverRosterImportIssue,
  OrderHandoverRosterImportPayload,
  OrderHandoverRosterImportPreview,
  OrderHandoverRosterImportRow,
  OrderHandoverRouteContext,
} from "../types";
import "./OrderHandoverDetailPage.css";

const { Text } = Typography;

type Props = {
  context: OrderHandoverRouteContext;
  onBack?: () => void;
  onImportSuccess?: () => void;
};

type RosterImportFormValues = {
  mode: OrderHandoverRosterImportMode;
};

type RosterImportFileDraft = {
  name: string;
  base64: string;
};

type RosterImportCellTone =
  | "error"
  | "space"
  | "newline"
  | "ambiguous-name"
  | "duplicate-certificate"
  | "duplicate-name-phone"
  | "birthday";

const ROSTER_IMPORT_FILE_ACCEPT = ".txt,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp";
const ROSTER_IMPORT_MAX_FILES = 50;
const ROSTER_IMPORT_TABLE_WIDTH = 1980;
const ROSTER_IMPORT_EDITABLE_FIELDS = [
  "name",
  "certificateNo",
  "phone",
  "classGroup",
  "teacherType",
  "actualReceivableAmount",
  "bigTrafficInfo",
  "guidanceNote",
  "studentNo",
  "role",
  "age",
  "gender",
  "birthday",
  "mainSignerAddress",
] as const satisfies readonly (keyof OrderHandoverRosterImportRow)[];

const ROSTER_IMPORT_FIELD_ALIASES: Partial<Record<keyof OrderHandoverRosterImportRow, string[]>> = {
  name: ["name", "member_name", "memberName", "traveler_name", "姓名"],
  certificateNo: ["certificate_no", "certificateNo", "id_card", "idCard", "身份证", "身份证号", "证件号"],
  phone: ["mobile", "phone", "mobile_phone", "tel", "telephone", "手机号", "手机号码", "电话"],
  classGroup: [
    "class_group",
    "classGroup",
    "class_group_label",
    "专业",
    "班级",
    "专业班级类型",
    "专业/班级类型",
    "专业、班级类型",
  ],
  teacherType: ["teacher_type", "teacherType", "teacher_type_label", "职务", "职位", "职务职位"],
  actualReceivableAmount: ["actual_receivable_amount", "actualReceivableAmount", "应收金额"],
  bigTrafficInfo: ["big_traffic_info", "bigTrafficInfo", "火车硬座硬卧", "大交通"],
  guidanceNote: ["guidance_note", "guidanceNote", "特殊情况", "备注"],
  studentNo: ["student_no", "studentNo", "学号"],
  role: ["traveler_type_label", "travelerTypeLabel", "role", "人员类型"],
  age: ["age", "年龄"],
  gender: ["gender", "性别"],
  birthday: ["birthday", "birth_date", "birthDate", "生日"],
  mainSignerAddress: ["main_signer_address", "mainSignerAddress", "主签合同人地址", "主签地址"],
};

const ROSTER_IMPORT_FIELD_LABELS: Partial<Record<keyof OrderHandoverRosterImportRow, string>> = {
  name: "姓名",
  certificateNo: "证件号",
  phone: "电话",
  classGroup: "专业/班级类型",
  teacherType: "职务/职位",
  actualReceivableAmount: "应收金额",
  bigTrafficInfo: "火车硬座硬卧",
  guidanceNote: "特殊情况",
  studentNo: "学号",
  role: "人员类型",
  age: "年龄",
  gender: "性别",
  birthday: "生日",
  mainSignerAddress: "主签合同人地址",
};

/** 订单交接名单导入独立工作台。 */
export function OrderHandoverRosterImportFeaturePage({ context, onBack, onImportSuccess }: Props) {
  const runtime = useOrderHandoverRuntime(context, { onRosterImportSuccess: onImportSuccess });
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUsePermission(currentUser, "customization.order_handover:manage");
  const [form] = Form.useForm<RosterImportFormValues>();
  const previewTimerRef = useRef<number | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const readingFilesRef = useRef(false);
  const [uploadedFileDrafts, setUploadedFileDrafts] = useState<RosterImportFileDraft[]>([]);
  const [editableRows, setEditableRows] = useState<OrderHandoverRosterImportRow[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const canCommit = Boolean(
    runtime.rosterImportPreview &&
      runtime.rosterImportPreview.summary.validRows > 0 &&
      !runtime.rosterImportPreviewing,
  );
  const parsingText = formatRosterImportParsingText(uploadedFileDrafts.length);

  useEffect(
    () => () => {
      if (previewTimerRef.current !== undefined) {
        window.clearTimeout(previewTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    setEditableRows(runtime.rosterImportPreview?.rows ?? []);
  }, [runtime.rosterImportPreview]);

  /** 清理名单解析防抖计时器。 */
  function clearPreviewTimer() {
    if (previewTimerRef.current === undefined) {
      return;
    }
    window.clearTimeout(previewTimerRef.current);
    previewTimerRef.current = undefined;
  }

  /** 立即解析上传文件或当前导入草稿。 */
  function previewNow(payload: OrderHandoverRosterImportPayload) {
    clearPreviewTimer();
    if (!hasRosterImportPayloadContent(payload)) {
      runtime.resetRosterImportPreview();
      return;
    }
    runtime.previewRosterImport(payload);
  }

  /** 获取当前保存模式，并收敛表单库的宽泛返回类型。 */
  function getImportMode(): OrderHandoverRosterImportMode {
    return form.getFieldValue("mode") === "replace" ? "replace" : "merge";
  }

  /** 构造当前导入入口的预览请求。 */
  function buildSourcePayload(mode: OrderHandoverRosterImportMode): OrderHandoverRosterImportPayload {
    return {
      mode,
      parseMode: "auto",
      files: uploadedFileDrafts.map((file) => ({
        fileName: file.name,
        fileContentBase64: file.base64,
      })),
    };
  }

  /** 构造提交给后端的名单导入请求。 */
  function buildCommitPayload(values: RosterImportFormValues): OrderHandoverRosterImportPayload {
    const sourcePayload = buildSourcePayload(values.mode ?? "merge");
    if (runtime.rosterImportPreview) {
      return {
        mode: sourcePayload.mode,
        parseMode: sourcePayload.parseMode,
        editedRows: editableRows
          .filter((row) => row.action !== "error")
          .map((row) => ({
            rowNumber: row.rowNumber,
            memberId: row.memberId,
            name: row.name,
            certificateNo: row.certificateNo,
            phone: row.phone,
            role: row.role,
            classGroup: row.classGroup,
            teacherType: row.teacherType,
            actualReceivableAmount: row.actualReceivableAmount,
            bigTrafficInfo: row.bigTrafficInfo,
            guidanceNote: row.guidanceNote,
            studentNo: row.studentNo,
            age: row.age,
            gender: row.gender,
            birthday: row.birthday,
            mainSignerAddress: row.mainSignerAddress,
          })),
      };
    }
    return sourcePayload;
  }

  /** 导入保存模式变化后重新解析当前草稿。 */
  function handleImportModeChange(mode: OrderHandoverRosterImportMode) {
    previewNow(buildSourcePayload(mode));
  }

  /** 读取名单文件并交由后端自动识别标准模板或 AI 解析。 */
  function processRosterFiles(files: File[]) {
    if (files.length === 0 || readingFilesRef.current || runtime.rosterImportPreviewing || runtime.rosterImportCommitting) {
      return;
    }
    const unsupported = files.find((file) => !isRosterImportFileSupported(file.name));
    if (unsupported) {
      Message.warning("名单导入仅支持 txt、Word、Excel、CSV 和图片文件");
      return;
    }
    const limitedFiles = files.slice(0, ROSTER_IMPORT_MAX_FILES);
    if (files.length > ROSTER_IMPORT_MAX_FILES) {
      Message.warning(`名单导入最多选择 ${ROSTER_IMPORT_MAX_FILES} 个文件`);
    }
    readingFilesRef.current = true;
    Promise.all(limitedFiles.map((file) => readFileAsBase64(file).then((base64) => ({ name: file.name, base64 }))))
      .then((drafts) => {
        setUploadedFileDrafts(drafts);
        previewNow({
          mode: getImportMode(),
          parseMode: "auto",
          files: drafts.map((file) => ({
            fileName: file.name,
            fileContentBase64: file.base64,
          })),
        });
      })
      .catch(() => {
        Message.error("名单文件读取失败，请重新选择");
      })
      .finally(() => {
        readingFilesRef.current = false;
      });
  }

  /** 处理文件选择框上传。 */
  function handleRosterFilesChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    processRosterFiles(files);
  }

  /** 处理名单文件拖拽上传。 */
  function handleRosterFilesDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    processRosterFiles(Array.from(event.dataTransfer.files));
  }

  /** 清除已上传文件，并清空解析结果。 */
  function handleClearFile() {
    setUploadedFileDrafts([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    runtime.resetRosterImportPreview();
  }

  /** 清空当前导入草稿和解析结果，回到可重新粘贴的状态。 */
  function handleClearImportDraft() {
    clearPreviewTimer();
    setUploadedFileDrafts([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setEditableRows([]);
    runtime.resetRosterImportPreview();
  }

  /** 修改预览行字段。 */
  function handlePreviewRowChange(
    rowNumber: number,
    field: keyof OrderHandoverRosterImportRow,
    value: string,
  ) {
    setEditableRows((rows) =>
      rows.map((row) =>
        row.rowNumber === rowNumber
          ? {
              ...row,
              [field]: field === "age" ? (value.trim() ? Number(value) : undefined) : value,
            }
          : row,
      ),
    );
  }

  /** 提交当前解析结果中的有效名单行。 */
  function handleCommit() {
    form.validate().then((values) => {
      runtime.commitRosterImport(buildCommitPayload(values));
    });
  }

  return (
    <main className="order-handover-page order-handover-roster-import-page">
      <div className="order-handover-import-head">
        <div>
          <Text className="order-handover-muted">出行名单</Text>
          <h1>导入名单工作台</h1>
          <p>{runtime.summary?.projectTitle ?? context.projectCode ?? context.projectId}</p>
        </div>
        {onBack ? (
          <Button icon={<ArrowLeft size={16} />} onClick={onBack}>
            返回订单交接
          </Button>
        ) : null}
      </div>

      <Card className="order-handover-roster-import-card" bordered>
        <RosterImportLegacyGuide onClear={handleClearImportDraft} />
        <div
          className="order-handover-roster-import-layout"
          data-drag-active={dragActive ? "true" : "false"}
          onDragEnter={(event) => {
            if (!event.dataTransfer.types.includes("Files")) return;
            event.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(event) => {
            if (!event.dataTransfer.types.includes("Files")) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = runtime.rosterImportPreviewing || runtime.rosterImportCommitting ? "none" : "copy";
            setDragActive(true);
          }}
          onDragLeave={(event) => {
            if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
            setDragActive(false);
          }}
          onDrop={handleRosterFilesDrop}
        >
          <Form
            form={form}
            className="order-handover-roster-import-source-panel"
            data-testid="roster-import-source-panel"
            data-layout="toolbar-compact"
            data-density="single-row"
            layout="vertical"
            initialValues={{
              mode: "merge",
            }}
          >
            <Form.Item field="mode" label="保存策略">
              <Radio.Group onChange={handleImportModeChange}>
                <Radio value="merge">合并更新</Radio>
                <Radio value="replace">替换名单</Radio>
              </Radio.Group>
            </Form.Item>
            <div
              className="order-handover-roster-import-upload-panel"
              data-testid="roster-import-upload-panel"
              data-entry="unified-file-upload"
              data-drag-active={dragActive ? "true" : "false"}
            >
              <input
                ref={fileInputRef}
                data-testid="roster-import-file-input"
                type="file"
                accept={ROSTER_IMPORT_FILE_ACCEPT}
                multiple
                hidden
                onChange={handleRosterFilesChange}
              />
              <Button icon={<UploadIcon size={16} />} onClick={() => fileInputRef.current?.click()}>
                上传文件导入
              </Button>
              <span>
                {uploadedFileDrafts.length > 0
                  ? uploadedFileDrafts.map((file) => file.name).join("、")
                  : "未选择文件"}
              </span>
              {uploadedFileDrafts.length > 0 ? (
                <Button size="mini" type="text" onClick={handleClearFile}>
                  清除文件
                </Button>
              ) : null}
            </div>

            <div className="order-handover-roster-import-actions">
              <Space>
                <Button
                  type="primary"
                  icon={<FileSpreadsheet size={16} />}
                  loading={runtime.rosterImportCommitting}
                  disabled={!canManage || !canCommit || runtime.rosterImportPreviewing}
                  onClick={handleCommit}
                >
                  导入有效行
                </Button>
                <Text className="order-handover-muted">
                  {runtime.rosterImportPreviewing
                    ? "正在解析"
                    : runtime.rosterImportPreview
                    ? `有效 ${runtime.rosterImportPreview.summary.validRows} 行`
                    : "等待解析"}
                </Text>
              </Space>
            </div>
            {runtime.rosterImportPreviewing ? (
              <div
                className="order-handover-roster-import-progress"
                role="progressbar"
                aria-label="名单解析进度"
                aria-valuetext={parsingText}
              >
                <Progress percent={45} showText={false} size="small" />
                <span>{parsingText}</span>
              </div>
            ) : null}
          </Form>

          <section className="order-handover-roster-import-result">
            {runtime.rosterImportErrorMessage ? (
              <Alert type="warning" content={runtime.rosterImportErrorMessage} showIcon />
            ) : null}
            {runtime.rosterImportPreview ? (
              <RosterImportResultPanel
                preview={runtime.rosterImportPreview}
                rows={editableRows}
                onRowChange={handlePreviewRowChange}
              />
            ) : (
              <div className="order-handover-roster-import-empty">
                <FileSpreadsheet size={32} />
                <strong>等待导入数据</strong>
                <span>将名单文件拖拽到此处，或点击“上传文件导入”，自动解析可导入行和错误行。</span>
              </div>
            )}
          </section>
        </div>
      </Card>
    </main>
  );
}

/** 呈现名单导入校验说明和基础操作。 */
function RosterImportLegacyGuide({
  onClear,
}: {
  onClear: () => void;
}) {
  return (
    <section className="order-handover-roster-import-guide">
      <div className="order-handover-roster-import-guide-title">出行名单导入校验说明</div>
      <div className="order-handover-roster-import-guide-body">
        <div className="order-handover-roster-import-legend-grid">
          <RosterImportLegendItem tone="error" text="红色表示错误" />
        </div>
        <Space className="order-handover-roster-import-guide-actions" size={4}>
          <Button size="mini" type="text" icon={<Eraser size={14} />} onClick={onClear}>
            清除
          </Button>
        </Space>
      </div>
    </section>
  );
}

/** 单条颜色说明。 */
function RosterImportLegendItem({
  tone,
  text,
}: {
  tone: RosterImportCellTone;
  text: string;
}) {
  return (
    <span className="order-handover-roster-import-legend-item">
      <i className={`is-${tone}`} aria-hidden="true" />
      {text}
    </span>
  );
}

/** 渲染名单解析结果。 */
function RosterImportResultPanel({
  preview,
  rows,
  onRowChange,
}: {
  preview: OrderHandoverRosterImportPreview;
  rows: OrderHandoverRosterImportRow[];
  onRowChange: (
    rowNumber: number,
    field: keyof OrderHandoverRosterImportRow,
    value: string,
  ) => void;
}) {
  const hasIssues = preview.issues.length > 0 || preview.summary.errorCount > 0;
  return (
    <div className="order-handover-roster-import-preview">
        <div className="order-handover-roster-import-preview-head">
        <strong>待导入名单</strong>
        <span>系统已完成解析，有效行可直接导入，错误行需修正后再处理。</span>
      </div>
      <div className="order-handover-roster-import-stats">
        <span>总行 {preview.summary.totalRows}</span>
        <span>有效 {preview.summary.validRows}</span>
        <span>新增 {preview.summary.insertCount}</span>
        <span>更新 {preview.summary.updateCount}</span>
        <span>停用 {preview.summary.stopCount}</span>
        <span className={hasIssues ? "is-warning" : undefined}>错误 {preview.summary.errorCount}</span>
      </div>
      {hasIssues ? (
        <Alert
          type="warning"
          content={`存在 ${preview.summary.errorCount} 条错误、${preview.summary.missingRequiredCount} 条必填缺失；如已有有效行，可先导入有效行。`}
          showIcon
        />
      ) : null}
      <div
        className="order-handover-roster-import-table-wrap"
        data-testid="roster-import-preview-table"
        data-pagination="none"
        data-table-width="fixed"
      >
        <Table
          rowKey={(row) => `${row.rowNumber}-${row.name}-${row.action}`}
          rowClassName={(row) => getRosterImportRowClassName(row)}
          columns={[
            { title: "行号", dataIndex: "rowNumber", width: 64 },
            { title: "动作", width: 72, render: (_, row) => formatImportAction(row.action) },
            {
              title: "姓名",
              width: 128,
              render: (_, row) => renderEditableImportCell(row, "name", "姓名", onRowChange),
            },
            {
              title: "证件号",
              width: 178,
              render: (_, row) => renderEditableImportCell(row, "certificateNo", "证件号", onRowChange),
            },
            {
              title: "电话",
              width: 128,
              render: (_, row) => renderEditableImportCell(row, "phone", "电话", onRowChange),
            },
            {
              title: "专业/班级类型",
              width: 138,
              render: (_, row) => renderEditableImportCell(row, "classGroup", "专业/班级类型", onRowChange),
            },
            {
              title: "职务/职位",
              width: 120,
              render: (_, row) => renderEditableImportCell(row, "teacherType", "职务/职位", onRowChange),
            },
            {
              title: "应收金额",
              width: 110,
              render: (_, row) =>
                renderEditableImportCell(row, "actualReceivableAmount", "应收金额", onRowChange),
            },
            {
              title: "火车硬座硬卧",
              width: 132,
              render: (_, row) => renderEditableImportCell(row, "bigTrafficInfo", "火车硬座硬卧", onRowChange),
            },
            {
              title: "特殊情况",
              width: 128,
              render: (_, row) => renderEditableImportCell(row, "guidanceNote", "特殊情况", onRowChange),
            },
            {
              title: "学号",
              width: 104,
              render: (_, row) => renderEditableImportCell(row, "studentNo", "学号", onRowChange),
            },
            {
              title: "人员类型",
              width: 96,
              render: (_, row) => renderEditableImportCell(row, "role", "人员类型", onRowChange),
            },
            {
              title: "年龄",
              width: 72,
              render: (_, row) => renderEditableImportCell(row, "age", "年龄", onRowChange),
            },
            {
              title: "性别",
              width: 72,
              render: (_, row) => renderEditableImportCell(row, "gender", "性别", onRowChange),
            },
            {
              title: "生日",
              width: 116,
              render: (_, row) => renderEditableImportCell(row, "birthday", "生日", onRowChange),
            },
            {
              title: "主签合同人地址",
              width: 168,
              render: (_, row) => renderEditableImportCell(row, "mainSignerAddress", "主签合同人地址", onRowChange),
            },
            {
              title: "问题",
              width: 160,
              render: (_, row) => renderRosterImportIssueChips(row),
            },
          ]}
          data={rows}
          pagination={false}
          border={false}
          size="small"
          tableLayoutFixed
          scroll={{ x: ROSTER_IMPORT_TABLE_WIDTH }}
        />
      </div>
    </div>
  );
}

/** 渲染问题列中的列名色块，和顶部图例保持一致。 */
function renderRosterImportIssueChips(row: OrderHandoverRosterImportRow) {
  if (row.issues.length === 0) {
    return <span className="order-handover-roster-import-issue-ok">可导入</span>;
  }
  return (
    <div className="order-handover-roster-import-issue-list">
      {row.issues.map((issue, index) => {
        const tone = getRosterImportIssueTone(issue, row);
        const fieldLabel = getRosterImportIssueFieldLabel(issue);
        const issueKey = issue.field ?? `row-${index}`;
        return (
          <span
            key={`${issueKey}-${issue.message}-${index}`}
            className={[
              "order-handover-roster-import-issue-chip",
              tone ? `is-${tone}` : undefined,
            ]
              .filter(Boolean)
              .join(" ")}
            data-testid={`roster-import-issue-chip-${row.rowNumber}-${issueKey}`}
            data-roster-import-issue-tone={tone}
            title={`${fieldLabel}：${issue.message}`}
          >
            <b>{fieldLabel}</b>
            <em>{issue.message}</em>
          </span>
        );
      })}
    </div>
  );
}

/** 渲染导入预览可编辑单元格。 */
function renderEditableImportCell(
  row: OrderHandoverRosterImportRow,
  field: keyof OrderHandoverRosterImportRow,
  label: string,
  onRowChange: (
    rowNumber: number,
    field: keyof OrderHandoverRosterImportRow,
    value: string,
  ) => void,
) {
  const rawValue = row[field];
  const value = rawValue === undefined || rawValue === null ? "" : String(rawValue);
  const tone = getRosterImportCellTone(row, field);
  const issueTitle = getRosterImportCellIssues(row, field)
    .map((issue) => issue.message)
    .join("；");
  return (
    <div
      className={[
        "order-handover-roster-import-edit-cell",
        tone ? `is-${tone}` : undefined,
      ]
        .filter(Boolean)
        .join(" ")}
      data-roster-import-cell-tone={tone}
      title={issueTitle || undefined}
    >
      <Input
        aria-label={`编辑导入${label}-第${row.rowNumber}行`}
        size="mini"
        value={value}
        onChange={(nextValue) => onRowChange(row.rowNumber, field, nextValue)}
      />
    </div>
  );
}

/** 获取字段级问题，兼容后端 snake_case 字段与旧系统中文提示。 */
function getRosterImportCellIssues(
  row: OrderHandoverRosterImportRow,
  field: keyof OrderHandoverRosterImportRow,
): OrderHandoverRosterImportIssue[] {
  return row.issues.filter((issue) => isRosterImportIssueForField(issue, field));
}

/** 根据字段级问题获取旧系统色块类型。 */
function getRosterImportCellTone(
  row: OrderHandoverRosterImportRow,
  field: keyof OrderHandoverRosterImportRow,
): RosterImportCellTone | undefined {
  const issues = getRosterImportCellIssues(row, field);
  if (issues.length === 0) {
    return undefined;
  }
  const issueText = issues.map((issue) => `${issue.field ?? ""}${issue.message}`).join(" ");
  if (isAmbiguousNameIssue(issueText)) {
    return "ambiguous-name";
  }
  if (isDuplicateNamePhoneIssue(issueText)) {
    return "duplicate-name-phone";
  }
  if (isDuplicateCertificateIssue(issueText)) {
    return "duplicate-certificate";
  }
  if (isBirthdayIssue(issueText)) {
    return "birthday";
  }
  if (isTrailingSpaceIssue(issueText)) {
    return "space";
  }
  if (isNewlineIssue(issueText)) {
    return "newline";
  }
  if (isPhoneOrCertificateErrorIssue(issueText)) {
    return "error";
  }
  return row.action === "error" ? "error" : undefined;
}

/** 根据单条问题获取问题列色块类型。 */
function getRosterImportIssueTone(
  issue: OrderHandoverRosterImportIssue,
  row: OrderHandoverRosterImportRow,
): RosterImportCellTone | undefined {
  const issueText = `${issue.field ?? ""}${issue.message}`;
  if (isAmbiguousNameIssue(issueText)) {
    return "ambiguous-name";
  }
  if (isDuplicateNamePhoneIssue(issueText)) {
    return "duplicate-name-phone";
  }
  if (isDuplicateCertificateIssue(issueText)) {
    return "duplicate-certificate";
  }
  if (isBirthdayIssue(issueText)) {
    return "birthday";
  }
  if (isTrailingSpaceIssue(issueText)) {
    return "space";
  }
  if (isNewlineIssue(issueText)) {
    return "newline";
  }
  if (isPhoneOrCertificateErrorIssue(issueText)) {
    return "error";
  }
  return row.action === "error" ? "error" : undefined;
}

/** 获取问题列里用于定位错误的字段中文名。 */
function getRosterImportIssueFieldLabel(issue: OrderHandoverRosterImportIssue): string {
  const issueText = `${issue.field ?? ""}${issue.message}`;
  if (isAmbiguousNameIssue(issueText)) {
    return "姓名";
  }
  if (isDuplicateNamePhoneIssue(issueText)) {
    return "手机号";
  }
  if (isDuplicateCertificateIssue(issueText)) {
    return "证件号";
  }
  if (isBirthdayIssue(issueText)) {
    return "生日";
  }
  for (const field of ROSTER_IMPORT_EDITABLE_FIELDS) {
    if (issue.field && matchesRosterImportField(issue.field, field)) {
      return ROSTER_IMPORT_FIELD_LABELS[field] ?? String(field);
    }
  }
  return issue.field ? issue.field : "整行";
}

/** 判断后端问题是否应标记在当前字段。 */
function isRosterImportIssueForField(
  issue: OrderHandoverRosterImportIssue,
  field: keyof OrderHandoverRosterImportRow,
): boolean {
  const issueText = `${issue.field ?? ""}${issue.message}`;
  if (isAmbiguousNameIssue(issueText)) {
    return field === "name";
  }
  if (isDuplicateNamePhoneIssue(issueText)) {
    return field === "name" || field === "phone";
  }
  if (isDuplicateCertificateIssue(issueText)) {
    return field === "certificateNo";
  }
  if (isBirthdayIssue(issueText)) {
    return field === "birthday";
  }
  if (!issue.field) {
    return false;
  }
  return matchesRosterImportField(issue.field, field);
}

/** 按字段别名匹配导入预览字段。 */
function matchesRosterImportField(
  issueField: string,
  field: keyof OrderHandoverRosterImportRow,
): boolean {
  const normalizedIssueField = normalizeRosterImportIssueText(issueField);
  const aliases = ROSTER_IMPORT_FIELD_ALIASES[field] ?? [String(field)];
  return aliases.some((alias) => normalizeRosterImportIssueText(alias) === normalizedIssueField);
}

/** 归一化后端字段名和中文问题文案，便于跨命名风格匹配。 */
function normalizeRosterImportIssueText(value: string): string {
  return value
    .toLowerCase()
    .replaceAll("【", "")
    .replaceAll("】", "")
    .replaceAll("[", "")
    .replaceAll("]", "")
    .replaceAll("（", "")
    .replaceAll("）", "")
    .replaceAll("(", "")
    .replaceAll(")", "")
    .replaceAll("_", "")
    .replaceAll("-", "")
    .replaceAll("+", "")
    .replaceAll("/", "")
    .replace(/[\s、，,：:；;]/g, "");
}

/** 判断是否为姓名重复导致无法确认更新对象的问题。 */
function isAmbiguousNameIssue(issueText: string): boolean {
  const normalized = normalizeRosterImportIssueText(issueText);
  return normalized.includes("姓名重复无法确认") || normalized.includes("ambiguousname");
}

/** 判断是否为手机号重复问题，兼容旧的姓名加电话重复提示。 */
function isDuplicateNamePhoneIssue(issueText: string): boolean {
  const normalized = normalizeRosterImportIssueText(issueText);
  return (
    normalized.includes("手机号重复") ||
    normalized.includes("电话重复") ||
    normalized.includes("姓名电话重复") ||
    normalized.includes("namephone")
  );
}

/** 判断是否为证件号重复问题。 */
function isDuplicateCertificateIssue(issueText: string): boolean {
  const normalized = normalizeRosterImportIssueText(issueText);
  return (
    normalized.includes("证件号重复") ||
    normalized.includes("身份证重复") ||
    normalized.includes("certificateduplicate")
  );
}

/** 判断是否为团中过生日提示。 */
function isBirthdayIssue(issueText: string): boolean {
  const normalized = normalizeRosterImportIssueText(issueText);
  return normalized.includes("团中过生日") || normalized.includes("birthday");
}

/** 判断是否为内容末尾空格问题。 */
function isTrailingSpaceIssue(issueText: string): boolean {
  const normalized = normalizeRosterImportIssueText(issueText);
  return normalized.includes("最后有空格") || normalized.includes("末尾空格") || normalized.includes("trailingspace");
}

/** 判断是否为 Excel 复制带换行或回车问题。 */
function isNewlineIssue(issueText: string): boolean {
  const normalized = normalizeRosterImportIssueText(issueText);
  return normalized.includes("换行") || normalized.includes("回车") || normalized.includes("newline");
}

/** 判断是否为手机号或证件号格式错误。 */
function isPhoneOrCertificateErrorIssue(issueText: string): boolean {
  const normalized = normalizeRosterImportIssueText(issueText);
  return (
    normalized.includes("手机号错误") ||
    normalized.includes("电话错误") ||
    normalized.includes("身份证号错误") ||
    normalized.includes("证件号错误") ||
    normalized.includes("mobileerror") ||
    normalized.includes("phoneerror") ||
    normalized.includes("certificateerror")
  );
}

/** 根据解析问题给预览行保留轻量标记，具体颜色落在单元格上。 */
function getRosterImportRowClassName(
  row: OrderHandoverRosterImportPreview["rows"][number],
): string {
  if (row.action === "error") {
    return "order-handover-roster-import-row has-error";
  }
  return "order-handover-roster-import-row";
}

/** 把导入动作映射为中文。 */
function formatImportAction(action: OrderHandoverRosterImportPreview["rows"][number]["action"]): string {
  if (action === "insert") {
    return "新增";
  }
  if (action === "update") {
    return "更新";
  }
  return "错误";
}

/** 把本地文件读取为后端可接收的 base64 内容。 */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.split(",").pop() ?? "" : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** 判断导入请求是否包含可解析内容。 */
function hasRosterImportPayloadContent(payload: OrderHandoverRosterImportPayload): boolean {
  return Boolean(
    payload.fileContentBase64 ||
      payload.text?.trim() ||
      (payload.files && payload.files.length > 0) ||
      (payload.editedRows && payload.editedRows.length > 0),
  );
}

/** 判断文件名是否属于名单统一导入支持范围。 */
function isRosterImportFileSupported(fileName: string): boolean {
  return /\.(txt|doc|docx|xls|xlsx|csv|png|jpe?g|webp)$/i.test(fileName);
}

/** 格式化名单解析中的状态文案。 */
function formatRosterImportParsingText(fileCount: number): string {
  return fileCount > 0 ? `正在解析 ${fileCount} 个文件` : "正在解析名单";
}
